import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { inquiryApi, type Inquiry } from "@/lib/api";
import Footer from "@/pages/home/components/Footer";
import ScrollReveal from "@/components/ScrollReveal";
import InquiryForm from "@/pages/inquiries/components/InquiryForm";
import InquiryList from "@/pages/inquiries/components/InquiryList";

const PAGE_SIZE = 10;

export default function InquiriesPage() {
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);

  const fetchInquiries = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const { data, total } = await inquiryApi.list(page, PAGE_SIZE);

      setInquiries(data || []);
      setTotalCount(total);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "문의 목록을 불러오지 못했습니다.";
      setError(msg);
      setInquiries([]);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchInquiries();
  }, [fetchInquiries]);

  const handleToggleExpand = (id: number) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    fetchInquiries();
  };

  const handleFormCancel = () => {
    setShowForm(false);
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "상담 문의 게시판 - 우공이산",
            "description": "우공이산 상담 문의 내역입니다. 원목칩, 가지칩, 뿌리 파쇄 관련 문의를 확인하실 수 있습니다.",
            "url": "https://www.jigugnc.co.kr/inquiries",
            "isPartOf": {
              "@type": "WebSite",
              "name": "우공이산",
              "url": "https://www.jigugnc.co.kr/"
            },
            "about": {
              "@type": "LocalBusiness",
              "name": "우공이산",
              "description": "원목칩·가지칩·뿌리 파쇄 전문 회사",
              "telephone": "+82-10-8079-0428",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "명동 990-9",
                "addressLocality": "양산시",
                "addressRegion": "경상남도",
                "addressCountry": "KR"
              }
            }
          })
        }}
      />
      <main className="min-h-screen bg-background-50 pt-20">
        <section className="py-16 md:py-24">
          <div className="max-w-5xl mx-auto px-6 md:px-10">
            <div className="mb-6">
              <button
                onClick={() => navigate("/")}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md border border-background-200 hover:border-primary-400 hover:bg-primary-50 text-foreground-600 hover:text-primary-700 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
                홈으로 돌아가기
              </button>
            </div>

            <div className="text-center mb-14">
              <ScrollReveal direction="up" delay={100}>
                <div className="inline-flex items-center gap-2 text-primary-700 text-xs tracking-[0.3em] uppercase font-medium mb-4">
                  <span className="w-8 h-px bg-primary-500"></span>
                  Inquiry Board
                </div>
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950">
                  상담 문의 게시판
                </h1>
                <p className="mt-4 text-foreground-700 text-base max-w-xl mx-auto">
                  고객님들이 남겨주신 상담 문의 내역입니다. 접수된 문의는 담당자가
                  순차적으로 확인 후 연락드립니다.
                </p>
              </ScrollReveal>
            </div>

            <div className="flex justify-end mb-6">
              <button
                onClick={() => setShowForm((s) => !s)}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className={`${showForm ? "ri-close-line" : "ri-edit-line"} w-4 h-4 flex items-center justify-center`}></i>
                {showForm ? "닫기" : "글쓰기"}
              </button>
            </div>

            {showForm && (
              <InquiryForm onSuccess={handleFormSuccess} onCancel={handleFormCancel} />
            )}

            <InquiryList
              inquiries={inquiries}
              loading={loading}
              error={error}
              page={page}
              totalCount={totalCount}
              expandedId={expandedId}
              onToggleExpand={handleToggleExpand}
              onPageChange={setPage}
              onRetry={fetchInquiries}
              onShowForm={() => setShowForm(true)}
            />
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}